import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["section", "button"]

  setupFormValidation() {
    const form = document.querySelector("#question-form-section form");
    if (form) {
      // Remove existing listeners to avoid duplicates
      form.removeEventListener("submit", this.validateForm.bind(this));
      
      // Add form submission validation
      form.addEventListener("submit", this.validateForm.bind(this));
      
      // Add real-time validation on input blur
      const inputs = form.querySelectorAll("input, textarea");
      inputs.forEach(input => {
        input.removeEventListener("blur", this.validateField.bind(this));
        input.removeEventListener("input", this.clearFieldError.bind(this));
        input.addEventListener("blur", this.validateField.bind(this));
        input.addEventListener("input", this.clearFieldError.bind(this));
      });
    }
  }

  show(event) {
    event.preventDefault();
    
    // Find the question form section
    const section = document.getElementById("question-form-section");
    if (section) { 
      section.classList.remove("d-none");
      // Setup form validation now that form is visible
      this.setupFormValidation();
    }
    
    // Hide the button that triggered the form
    event.target.style.display = "none";
    
    // Focus on the first input
    const firstInput = section?.querySelector("input[type='text']");
    if (firstInput) { setTimeout(() => firstInput.focus(), 100); }
  }

  hide(event) {
    event.preventDefault();
    
    // Find the question form section
    const section = document.getElementById("question-form-section");
    if (section) { section.classList.add("d-none"); }
    
    // Show the button again
    const button = this.element.querySelector("button[data-action*='show']");
    if (button) { button.style.display = "inline-block"; }
    
    // Clear the form
    const form = section?.querySelector("form");
    if (form) { 
      form.reset();
      this.clearValidationErrors();
    }
  }

  validateForm(event) {
    const form = event.target;
    let isValid = true;
    
    // Clear previous validation errors
    this.clearValidationErrors();
    
    // Get form fields
    const nameField = form.querySelector("input[name='question[name]']");
    const emailField = form.querySelector("input[name='question[email]']");
    const messageField = form.querySelector("textarea[name='question[message]']");
    
    // Validate name
    if (!nameField?.value.trim()) {
      this.showFieldError(nameField, "Ime i prezime je obavezno polje.");
      isValid = false;
    } else if (nameField.value.trim().length < 2) {
      this.showFieldError(nameField, "Ime mora imati najmanje 2 znaka.");
      isValid = false;
    }
    
    // Validate email
    if (!emailField?.value.trim()) {
      this.showFieldError(emailField, "Email adresa je obavezno polje.");
      isValid = false;
    } else if (!this.isValidEmail(emailField.value.trim())) {
      this.showFieldError(emailField, "Molimo unesite valjanu email adresu.");
      isValid = false;
    }
    
    // Validate message
    if (!messageField?.value.trim()) {
      this.showFieldError(messageField, "Pitanje je obavezno polje.");
      isValid = false;
    } else if (messageField.value.trim().length < 10) {
      this.showFieldError(messageField, "Pitanje mora imati najmanje 10 znakova.");
      isValid = false;
    }
    
    if (!isValid) {
      event.preventDefault();
      event.stopPropagation();
      // Focus on first invalid field
      const firstInvalidField = form.querySelector(".is-invalid");
      if (firstInvalidField) {
        firstInvalidField.focus();
      }
    } else {
      // Show loading state
      this.showLoadingState(event.target);
    }
  }

  showLoadingState(form) {
    const submitButton = form.querySelector("input[type='submit'], button[type='submit']");
    if (submitButton) {
      submitButton.disabled = true;
      submitButton.textContent = "Šalje se...";
    }
  }

  showFieldError(field, message) {
    // Add error class to field
    field.classList.add("is-invalid");
    
    // Create or update error message
    const wrapper = field.closest(".form-group") || field.closest("div");
    let errorElement = wrapper.querySelector(".invalid-feedback");
    
    if (!errorElement) {
      errorElement = document.createElement("div");
      errorElement.className = "invalid-feedback";
      wrapper.appendChild(errorElement);
    }
    
    errorElement.textContent = message;
    errorElement.style.display = "block";
  }

  clearValidationErrors() {
    const form = document.getElementById("question-form-section");
    if (form) {
      // Remove error classes
      form.querySelectorAll(".is-invalid").forEach(field => {
        field.classList.remove("is-invalid");
      });
      
      // Remove error messages
      form.querySelectorAll(".invalid-feedback").forEach(error => {
        error.remove();
      });
    }
  }

  validateField(event) {
    const field = event.target;
    const fieldName = field.name;
    
    // Clear previous error for this field
    this.clearFieldError(event);
    
    // Validate based on field
    if (fieldName === "question[name]") {
      if (!field.value.trim()) {
        this.showFieldError(field, "Ime i prezime je obavezno polje.");
      } else if (field.value.trim().length < 2) {
        this.showFieldError(field, "Ime mora imati najmanje 2 znaka.");
      }
    } else if (fieldName === "question[email]") {
      if (!field.value.trim()) {
        this.showFieldError(field, "Email adresa je obavezno polje.");
      } else if (!this.isValidEmail(field.value.trim())) {
        this.showFieldError(field, "Molimo unesite valjanu email adresu.");
      }
    } else if (fieldName === "question[message]") {
      if (!field.value.trim()) {
        this.showFieldError(field, "Pitanje je obavezno polje.");
      } else if (field.value.trim().length < 10) {
        this.showFieldError(field, "Pitanje mora imati najmanje 10 znakova.");
      }
    }
  }

  clearFieldError(event) {
    const field = event.target;
    field.classList.remove("is-invalid");
    
    const wrapper = field.closest(".form-group") || field.closest("div");
    const errorElement = wrapper.querySelector(".invalid-feedback");
    if (errorElement) {
      errorElement.remove();
    }
  }

  isValidEmail(email) {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }
}
